import { useEffect, useRef } from 'react';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';

// A dashboard row must have at least this many items to render.
export const MIN_ITEMS = 5;
// Hard cap on how many items a dashboard row ever displays.
export const MAX_ITEMS = 20;

interface DashboardRowFetchParams {
	// Identity of the current request; a change (new subject set, cascade level)
	// triggers a refetch.
	fetchKey: string;
	// Async status of the slot this row reads from.
	status: AsyncActionStatus;
	fetch: () => void;
	// Skip fetching entirely (e.g. no subjects). Defaults to true.
	enabled?: boolean;
	// Key the store slot currently holds. Pass for single-slot stores so a stale
	// key (subject change) refetches even while the status is settled. Omit for
	// per-key stores whose status is already INITIAL for a never-fetched key.
	loadedKey?: string;
}

/**
 * Shared fetch policy for dashboard rows: fetch when the slot has never been
 * loaded (INITIAL), holds a stale key, or last failed (ERROR), and skip a remount
 * that already has the matching data. Never fires while a request is in flight.
 *
 * `status` IS a dependency so the effect re-runs when an in-flight request settles:
 * for a single-slot store, a key change (A→B) while A is loading is deferred by the
 * LOADING guard, then re-evaluated when A completes so B is fetched (otherwise
 * `loadedKey` — set at START — never changes on completion and B would be lost).
 *
 * ERROR is retried on remount (one transient failure must not hide the row for the
 * whole SPA session) but only once per key: `lastAttemptedKey` records the key we
 * already fetched, so the START → ERROR cycle can't loop on a persistent failure.
 */
const useDashboardRowFetch = ({
	fetchKey,
	status,
	fetch,
	enabled = true,
	loadedKey,
}: DashboardRowFetchParams): void => {
	const lastAttemptedKey = useRef<string | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!enabled) return;
		if (status === AsyncActionStatus.LOADING) return;

		const neverLoaded = status === AsyncActionStatus.INITIAL;
		const staleKey = loadedKey !== undefined && loadedKey !== fetchKey;
		// Retry a failed key only once per mount, so ERROR → fetch → START → ERROR
		// can't loop; a fresh mount (new ref) or a different key retries as expected.
		const retryFailed = status === AsyncActionStatus.ERROR && lastAttemptedKey.current !== fetchKey;

		if (neverLoaded || staleKey || retryFailed) {
			lastAttemptedKey.current = fetchKey;
			fetch();
		}
	}, [fetchKey, status, enabled, loadedKey]);
};

export default useDashboardRowFetch;
